/**
 * Phase 7.9 — Bay lockout / tagout enforcement (pure helper + vocabulary).
 *
 * Doctrine:
 *   - Bucket 3 §3.3.3  A bay locked-out / tagged-out is physically
 *                      unavailable. It must not be used for OptiAssign,
 *                      cascade, manual moves, auto moves, splits,
 *                      rollover placement, specialty bay booking,
 *                      pull-forward, or parking-to-bay transition.
 *   - Bucket 3 §3.3.3  Lockout/tagout is a safety / equipment-availability
 *                      constraint, not a soft preference.
 *   - Bucket 3 §3.3.3  Causes of unavailability: maintenance, safety
 *                      lockout, equipment failure, lift failure,
 *                      calibration/ADAS equipment unavailable, inspection
 *                      block, manager override, other.
 *
 * Why this module exists
 * ----------------------
 * Read-first survey at Phase 7.9 confirmed:
 *
 *   - **Schema EXISTS** (migration `20251221030856`):
 *       `shop_bays.is_locked_out boolean DEFAULT false`
 *       `shop_bays.lockout_reason text`
 *       `shop_bays.locked_out_at timestamptz`
 *       `shop_bays.locked_out_by uuid (FK auth.users)`
 *     Plus the existing `shop_bays.is_active boolean` predates lockout
 *     but serves a similar exclusion purpose.
 *
 *   - **DB-level guard EXISTS** (migration `20260424010000`):
 *     `move_vehicle_location` RPC refuses placement when
 *     `is_locked_out = true`.
 *
 *   - **Runtime enforcement is PARTIAL** — present in 4 surfaces today
 *     but not centralized:
 *       - `smart-assign/index.ts:1583` — OptiAssign candidate filter.
 *       - `_shared/reassignCore.ts:203` — reassign candidate filter.
 *       - `_shared/pickCompatibleBayForTech.ts:54-58` — rollover Part B
 *         bay picker.
 *       - `_shared/placementRulesEnhanced.ts:103,166-194` — placement
 *         rules with `is_locked_out + lockout_reason` reporting.
 *
 *   - **Surfaces with verification gaps**: cascade (global / local),
 *     pull-forward, specialty bay booking, parking-to-bay transition.
 *     The DB-level `move_vehicle_location` RPC backstops manual
 *     moves and parking-to-bay; cascade / specialty / pull-forward
 *     need explicit helper adoption.
 *
 * Phase 7.9 contribution:
 *   1. Canonical pure classifier reading every plausible column
 *      shape (`is_locked_out` / `locked_out` / `tagged_out` /
 *      `is_tagged_out` / `out_of_service` / `is_out_of_service` /
 *      `status` / `bay_status` / `is_active` / `lockout_reason` /
 *      `unavailable_reason` / `maintenance_status`).
 *   2. Vocabulary: 10 lockout states + 12 blocker codes + 9
 *      placement surfaces.
 *   3. Surface-aware validator that emits the same blocker code
 *      with surface-specific routing.
 *   4. Source-scan tests pin the 4 existing inline enforcement
 *      sites so future edits cannot regress.
 *
 * Phase 7.9 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock reads, no React hooks. Caller
 *     passes in-memory bay projections.
 *   - **No DB migration**. Schema already adequate
 *     (`is_locked_out` + `lockout_reason` + `is_active`).
 *   - No runtime wiring. Existing enforcement surfaces remain
 *     untouched; adoption points documented at the bottom for
 *     cascade / pull-forward / specialty bay booking / split.
 *
 * Builds on:
 *   - Phase 7.7 `vehiclePhysicalReality.bay_type_mismatch` blocker
 *     vocabulary.
 *   - Phase 7.8 `specialtyBayEntryDecision` (specialty bay capacity
 *     invariants).
 *   - Phase 1.1 `getProductionState` (ticket-context resolver for
 *     `bay_required_for_ticket_but_unavailable`).
 */

// =============================================================================
// Vocabulary
// =============================================================================

/**
 * Discrete bay lockout / availability states. The helper resolves
 * one state per bay from a permissive set of fields — schema today
 * has `is_locked_out` + `lockout_reason`; the classifier accepts
 * additional shapes (`tagged_out`, `out_of_service`, `bay_status`,
 * `is_active`) so adopting callers can route alternate signals
 * without breaking the contract.
 */
export type BayLockoutState =
  | 'available'
  | 'locked_out'
  | 'tagged_out'
  | 'out_of_service'
  | 'maintenance'
  | 'equipment_failure'
  | 'inspection_blocked'
  | 'manager_blocked'
  | 'inactive'
  | 'unknown';

export const BAY_LOCKOUT_STATES = Object.freeze([
  'available',
  'locked_out',
  'tagged_out',
  'out_of_service',
  'maintenance',
  'equipment_failure',
  'inspection_blocked',
  'manager_blocked',
  'inactive',
  'unknown',
] as const) satisfies ReadonlyArray<BayLockoutState>;

/**
 * Discrete blocker codes. Routed alongside the surface to give the
 * UI / audit a precise reason for refusing placement.
 */
export type BayLockoutBlocker =
  | 'bay_locked_out'
  | 'bay_tagged_out'
  | 'bay_out_of_service'
  | 'bay_under_maintenance'
  | 'bay_equipment_failure'
  | 'bay_inspection_blocked'
  | 'bay_manager_blocked'
  | 'bay_inactive'
  | 'bay_unavailable_unknown_reason'
  | 'bay_required_for_ticket_but_unavailable'
  | 'specialty_equipment_unavailable'
  | 'lockout_context_missing';

export const BAY_LOCKOUT_BLOCKERS = Object.freeze([
  'bay_locked_out',
  'bay_tagged_out',
  'bay_out_of_service',
  'bay_under_maintenance',
  'bay_equipment_failure',
  'bay_inspection_blocked',
  'bay_manager_blocked',
  'bay_inactive',
  'bay_unavailable_unknown_reason',
  'bay_required_for_ticket_but_unavailable',
  'specialty_equipment_unavailable',
  'lockout_context_missing',
] as const) satisfies ReadonlyArray<BayLockoutBlocker>;

/**
 * Discrete placement surfaces. The validator emits the SAME blocker
 * regardless of surface — but the surface field travels in the
 * verdict so callers can route the message to the right UI /
 * audit channel.
 */
export type BayPlacementSurface =
  | 'optiassign'
  | 'cascade'
  | 'manual_move'
  | 'auto_move'
  | 'split'
  | 'rollover'
  | 'specialty_booking'
  | 'pull_forward'
  | 'parking_to_bay_transition';

export const BAY_PLACEMENT_SURFACES = Object.freeze([
  'optiassign',
  'cascade',
  'manual_move',
  'auto_move',
  'split',
  'rollover',
  'specialty_booking',
  'pull_forward',
  'parking_to_bay_transition',
] as const) satisfies ReadonlyArray<BayPlacementSurface>;

// =============================================================================
// Input shapes
// =============================================================================

/**
 * Permissive bay shape. The helper reads only what is named here
 * and tolerates additional caller-supplied fields. The canonical
 * schema fields (`is_locked_out`, `lockout_reason`, `is_active`)
 * are the primary signals; alternate signals are accepted so
 * adopting callers can wire alternate sources without schema churn.
 */
export interface BayLockoutShape {
  id?: string | null;
  /** Canonical schema column (migration `20251221030856`). */
  is_locked_out?: boolean | null;
  locked_out?: boolean | null;
  /** Canonical lockout reason column. */
  lockout_reason?: string | null;
  locked_out_at?: string | Date | null;
  locked_out_by?: string | null;
  /** Tagged-out alternates (no schema column today). */
  tagged_out?: boolean | null;
  is_tagged_out?: boolean | null;
  tagout_reason?: string | null;
  /** Out-of-service alternates. */
  out_of_service?: boolean | null;
  is_out_of_service?: boolean | null;
  /** Generic status fields. */
  status?: string | null;
  bay_status?: string | null;
  maintenance_status?: string | null;
  /** Canonical activation flag. */
  is_active?: boolean | null;
  /** Free-form unavailability reason (alternate). */
  unavailable_reason?: string | null;
  /** Optional bay metadata for routing. */
  bay_type?: string | null;
  name?: string | null;
}

/**
 * Optional context for placement validation. Currently lightweight;
 * future Phase 7.10+ may extend with active-booking detection.
 */
export interface BayPlacementContext {
  /**
   * When true, inactive bays (`is_active === false`) emit
   * `bay_inactive`. Default true — inactive bays should be excluded
   * from every placement surface today.
   */
  treatInactiveAsUnavailable?: boolean;
  /**
   * When set, the helper compares this ticket's required bay type
   * against the bay's `bay_type` to surface
   * `bay_required_for_ticket_but_unavailable`. Caller passes the
   * required bay type derived from job-line context.
   */
  requiredBayType?: string | null;
  /** Free-form context label for audit routing. */
  contextLabel?: string | null;
}

// =============================================================================
// Internal helpers
// =============================================================================

function pushUnique<T>(arr: T[], v: T): void {
  if (!arr.includes(v)) arr.push(v);
}

function normalizeStatus(s: unknown): string | null {
  if (typeof s !== 'string') return null;
  return s.trim().toLowerCase();
}

/**
 * Discrete `status` / `bay_status` / `maintenance_status` values
 * mapped to canonical lockout states. The mapping is intentionally
 * generous — callers using any synonym get the right blocker.
 */
const STATUS_TO_STATE: Record<string, BayLockoutState> = {
  // Hard lockout.
  locked: 'locked_out',
  lockedout: 'locked_out',
  locked_out: 'locked_out',
  // Tagout.
  tagged: 'tagged_out',
  taggedout: 'tagged_out',
  tagged_out: 'tagged_out',
  // Out of service.
  oos: 'out_of_service',
  out_of_service: 'out_of_service',
  outofservice: 'out_of_service',
  offline: 'out_of_service',
  unavailable: 'out_of_service',
  // Maintenance.
  maintenance: 'maintenance',
  in_maintenance: 'maintenance',
  under_maintenance: 'maintenance',
  servicing: 'maintenance',
  // Equipment failure.
  equipment_failure: 'equipment_failure',
  lift_failure: 'equipment_failure',
  alignment_failure: 'equipment_failure',
  adas_failure: 'equipment_failure',
  failure: 'equipment_failure',
  // Inspection.
  inspection_blocked: 'inspection_blocked',
  inspection_required: 'inspection_blocked',
  blocked_for_inspection: 'inspection_blocked',
  // Manager block.
  manager_blocked: 'manager_blocked',
  manager_hold: 'manager_blocked',
  admin_blocked: 'manager_blocked',
  // Inactive (typically `is_active=false` but caller may set status).
  inactive: 'inactive',
  disabled: 'inactive',
  // Available.
  active: 'available',
  available: 'available',
  ok: 'available',
  ready: 'available',
};

function stateForStatus(s: string | null): BayLockoutState | null {
  if (s == null) return null;
  return STATUS_TO_STATE[s] ?? null;
}

const STATE_TO_BLOCKER: Record<BayLockoutState, BayLockoutBlocker | null> = {
  available: null,
  locked_out: 'bay_locked_out',
  tagged_out: 'bay_tagged_out',
  out_of_service: 'bay_out_of_service',
  maintenance: 'bay_under_maintenance',
  equipment_failure: 'bay_equipment_failure',
  inspection_blocked: 'bay_inspection_blocked',
  manager_blocked: 'bay_manager_blocked',
  inactive: 'bay_inactive',
  unknown: 'bay_unavailable_unknown_reason',
};

// =============================================================================
// Classifier
// =============================================================================

/**
 * Resolve the canonical lockout state for a single bay. Pure.
 *
 * Resolution order (terminal-first; first match wins):
 *
 *   1. Explicit `is_locked_out === true` OR `locked_out === true` →
 *      `locked_out`.
 *   2. Explicit `tagged_out === true` OR `is_tagged_out === true` →
 *      `tagged_out`.
 *   3. Explicit `out_of_service === true` OR `is_out_of_service === true`
 *      → `out_of_service`.
 *   4. `bay_status` / `status` / `maintenance_status` mapped via
 *      `STATUS_TO_STATE` (first non-available match wins).
 *   5. `is_active === false` (AND `treatInactiveAsUnavailable !== false`) →
 *      `inactive`.
 *   6. `lockout_reason` / `unavailable_reason` non-empty WITHOUT a flag
 *      → `unknown` (the reason suggests unavailability but no flag
 *      committed; emit `unknown` so callers see it but can recover).
 *   7. otherwise → `available`.
 */
export function getBayLockoutState(
  bay: BayLockoutShape,
  context?: BayPlacementContext,
): BayLockoutState {
  if (bay == null) return 'unknown';

  // 1-3. Explicit boolean flags.
  if (bay.is_locked_out === true || bay.locked_out === true) return 'locked_out';
  if (bay.tagged_out === true || bay.is_tagged_out === true) return 'tagged_out';
  if (bay.out_of_service === true || bay.is_out_of_service === true) {
    return 'out_of_service';
  }

  // 4. Status string fields. Check in priority order: maintenance_status
  // (most specific) → bay_status → status.
  const fromMaintenance = stateForStatus(normalizeStatus(bay.maintenance_status));
  if (fromMaintenance != null && fromMaintenance !== 'available') {
    return fromMaintenance;
  }
  const fromBayStatus = stateForStatus(normalizeStatus(bay.bay_status));
  if (fromBayStatus != null && fromBayStatus !== 'available') {
    return fromBayStatus;
  }
  const fromStatus = stateForStatus(normalizeStatus(bay.status));
  if (fromStatus != null && fromStatus !== 'available') {
    return fromStatus;
  }

  // 5. is_active flag.
  if (
    bay.is_active === false &&
    (context?.treatInactiveAsUnavailable !== false)
  ) {
    return 'inactive';
  }

  // 6. Reason field WITHOUT a flag — defensive.
  const hasUnexplainedReason =
    (typeof bay.lockout_reason === 'string' && bay.lockout_reason.trim().length > 0) ||
    (typeof bay.unavailable_reason === 'string' && bay.unavailable_reason.trim().length > 0) ||
    (typeof bay.tagout_reason === 'string' && bay.tagout_reason.trim().length > 0);
  if (hasUnexplainedReason) return 'unknown';

  // 7. Default available.
  return 'available';
}

// =============================================================================
// Predicates
// =============================================================================

export function isBayLockedOut(
  bay: BayLockoutShape,
  context?: BayPlacementContext,
): boolean {
  return getBayLockoutState(bay, context) === 'locked_out';
}

export function isBayTaggedOut(
  bay: BayLockoutShape,
  context?: BayPlacementContext,
): boolean {
  return getBayLockoutState(bay, context) === 'tagged_out';
}

/**
 * Boolean: is this bay usable for ANY placement surface? True iff
 * the resolved state is `available`.
 */
export function isBayUsableForPlacement(
  bay: BayLockoutShape,
  context?: BayPlacementContext,
): boolean {
  return getBayLockoutState(bay, context) === 'available';
}

// =============================================================================
// Verdict / validator
// =============================================================================

export interface BayPlacementVerdict {
  bayId: string;
  state: BayLockoutState;
  surface: BayPlacementSurface | null;
  /** Empty when bay is usable. */
  blockers: BayLockoutBlocker[];
  /**
   * Human-readable reason from `lockout_reason` /
   * `unavailable_reason` / `tagout_reason` (whichever is set). `null`
   * when no reason field is populated.
   */
  reason: string | null;
  /** True iff `blockers.length === 0`. */
  ok: boolean;
}

function resolveReason(bay: BayLockoutShape): string | null {
  for (const field of ['lockout_reason', 'tagout_reason', 'unavailable_reason'] as const) {
    const v = (bay as Record<string, unknown>)[field];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return null;
}

/**
 * Get the canonical placement eligibility for a single bay against
 * an optional surface. Pure.
 *
 * When `state === 'available'`, returns `ok: true` with no blockers.
 * Otherwise emits the canonical blocker for the state.
 *
 * The `surface` field is recorded for audit routing but does NOT
 * change the blocker code — every placement surface in the
 * directive's list excludes locked / tagged / OOS / maintenance
 * bays equally.
 */
export function getBayPlacementEligibility(
  bay: BayLockoutShape,
  surface?: BayPlacementSurface | null,
  context?: BayPlacementContext,
): BayPlacementVerdict {
  const state = getBayLockoutState(bay, context);
  const blockers: BayLockoutBlocker[] = [];
  if (state !== 'available') {
    const code = STATE_TO_BLOCKER[state];
    if (code != null) pushUnique(blockers, code);
  }

  // Specialty equipment / required-bay context.
  if (
    context?.requiredBayType != null &&
    typeof bay.bay_type === 'string' &&
    bay.bay_type !== context.requiredBayType &&
    state !== 'available'
  ) {
    pushUnique(blockers, 'bay_required_for_ticket_but_unavailable');
  }

  return {
    bayId: String(bay?.id ?? ''),
    state,
    surface: surface ?? null,
    blockers,
    reason: resolveReason(bay),
    ok: blockers.length === 0,
  };
}

/**
 * Flat blocker list for a single bay against an optional surface.
 * Convenience wrapper.
 */
export function getBayLockoutBlockers(
  bay: BayLockoutShape,
  surface?: BayPlacementSurface | null,
  context?: BayPlacementContext,
): BayLockoutBlocker[] {
  return getBayPlacementEligibility(bay, surface, context).blockers;
}

/**
 * Validate a proposed placement of a ticket onto a bay against
 * lockout/tagout. The ticket is currently informational; future
 * Phase 7.10+ can read job-line context to refine
 * `bay_required_for_ticket_but_unavailable`.
 */
export function validateBayPlacementAgainstLockout(
  ticket: { id?: string | null; bay_type?: string | null } | null | undefined,
  bay: BayLockoutShape,
  surface: BayPlacementSurface,
  context?: BayPlacementContext,
): BayPlacementVerdict {
  // Pull required bay type from the ticket if not in context.
  const requiredBayType =
    context?.requiredBayType ??
    (ticket && typeof ticket.bay_type === 'string' ? ticket.bay_type : null);
  return getBayPlacementEligibility(bay, surface, {
    ...context,
    requiredBayType,
  });
}

/**
 * Filter a list of bays down to those usable for a given placement
 * surface. Sister to the inline filters in `smart-assign`,
 * `reassignCore`, `pickCompatibleBayForTech`, and
 * `placementRulesEnhanced` — but pure (no DB).
 *
 * Bays missing `id` are excluded defensively (cannot route a
 * placement without an id).
 */
export function filterUsableBaysForPlacement(
  bays: ReadonlyArray<BayLockoutShape>,
  surface?: BayPlacementSurface | null,
  context?: BayPlacementContext,
): BayLockoutShape[] {
  const out: BayLockoutShape[] = [];
  for (const b of bays) {
    if (b == null || b.id == null) continue;
    if (isBayUsableForPlacement(b, context)) out.push(b);
  }
  return out;
}

/**
 * Convenience: throw a descriptive Error when the bay is not usable
 * for the surface. Mirrors the inline `Bay is locked out` message
 * pattern used by the DB-level `move_vehicle_location` guard.
 */
export function assertBayNotLockedOutForPlacement(
  bay: BayLockoutShape,
  surface: BayPlacementSurface,
  context?: BayPlacementContext,
): void {
  const verdict = getBayPlacementEligibility(bay, surface, context);
  if (verdict.ok) return;
  const reasonPart = verdict.reason ? ` (${verdict.reason})` : '';
  throw new Error(
    `Bay is ${verdict.state.replace(/_/g, ' ')} and cannot accept a placement on surface "${surface}"${reasonPart}. Blockers: ${verdict.blockers.join(', ')}`,
  );
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. **Already wired (4 surfaces)**:
//    - `supabase/functions/smart-assign/index.ts:1583-1586`
//      excludes `is_locked_out` from the OptiAssign candidate pool.
//    - `supabase/functions/_shared/reassignCore.ts:203` excludes
//      from reassign candidates.
//    - `supabase/functions/_shared/pickCompatibleBayForTech.ts:54-58`
//      excludes from rollover Part B bay picker.
//    - `supabase/functions/_shared/placementRulesEnhanced.ts:103,166-194`
//      checks `is_locked_out + lockout_reason` for placement
//      reporting.
//
// 2. **Verified by source-scan tests** (Phase 7.9 pins): the four
//    surfaces above use the canonical `is_locked_out` column. The
//    pins prevent regression if any of these filters is removed in
//    a future edit.
//
// 3. **Adoption gap — cascade**:
//    `globalCascade.ts` / `localCascade.ts` propose layout
//    changes but do NOT yet consult lockout. Adoption: thread the
//    bay set through `filterUsableBaysForPlacement(bays, 'cascade')`
//    BEFORE running the layout solver. The DB-level
//    `move_vehicle_location` guard already prevents the bad write,
//    but the cascade proposal would still surface the bay as a
//    candidate — UX confusion.
//
// 4. **Adoption gap — pull-forward**:
//    `pullForwardPlanner.ts` exposes blocker codes for tech /
//    capacity / specialty-bay-mismatch but does NOT yet exclude
//    locked-out bays. Adoption: extend
//    `PullForwardBlocker` with `bay_locked_out` and consult this
//    helper when the target slot is identified.
//
// 5. **Adoption gap — specialty bay booking**:
//    `src/utils/specialtyBayBooking.ts` queries `shop_bays` filtered
//    by `bay_type` + `is_active=true` but does NOT explicitly
//    filter `is_locked_out`. Adoption: add `.or('is_locked_out.is.null,is_locked_out.eq.false')`
//    to the query, then run `assertBayNotLockedOutForPlacement(bay,
//    'specialty_booking')` before INSERTing the booking.
//
// 6. **Adoption gap — split placement**:
//    `supabase/functions/execute-manual-split/index.ts:318` already
//    DELEGATES bay picking to `pickCompatibleBayForTech` (which is
//    wired). Adoption: source-scan that delegation to lock it.
//
// 7. **Manual moves / parking-to-bay**: the DB-level
//    `move_vehicle_location` RPC guard (migration `20260424010000`)
//    backstops these. The helper's `manual_move` and
//    `parking_to_bay_transition` surfaces give the UI / API layer a
//    way to refuse the call BEFORE hitting the RPC — better UX.
//
// 8. **Audit**: reserve `BAY_PLACEMENT_BLOCKED_LOCKOUT` in
//    `AdminActionType` when adoption emits audit rows.
//
// 9. **Future schema enrichment (optional Phase 7.10+)**: the
//    current `lockout_reason: text` is free-form. A future
//    migration could add `shop_bays.lockout_kind enum
//    ('maintenance' | 'safety_lockout' | 'equipment_failure' |
//    'lift_failure' | 'adas_unavailable' | 'inspection_block' |
//    'manager_override' | 'other')` to make analytics + UI routing
//    more precise. Helper's `STATUS_TO_STATE` map already accepts
//    these synonyms — adoption is non-breaking.
//
// 10. **Tagout / out-of-service columns (optional)**: today's schema
//     has only `is_locked_out`. The helper accepts `tagged_out`,
//     `out_of_service`, etc. so adopters using alternate columns
//     (e.g., custom shop deployments) work without re-tagging.
//     Future Phase 7.10+ can decide whether to formalize separate
//     columns or keep everything funneled through
//     `is_locked_out + lockout_reason`.
